/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Grid,
  Box,
  TextField,
  useMediaQuery,
  useTheme,
  Typography,
  Container,
  Autocomplete,
  InputAdornment,
  IconButton,
  Button,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useSelector } from "react-redux";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Close as CloseIcon, Check as CheckIcon } from "@mui/icons-material";
import { RootState, useAppDispatch } from "@store/store";
import {
  setAccountUser,
  setConditionLoan,
  setTaxProductId,
  setLoanStatus,
  setTaxSystemType,
  setGuaranteeTypeId,
  setGuaranteeTypes,
  setFinancedServicesId,
  setIsPreviousClient,
  setRfcApprovedCompany,
  setMotorbikeInformation,
  setConvenioId,
  setQuoterDetails,
} from "@store/slices/registerSlice";
import { useSaveLeadDataMutation } from "@api/onboarding";
import { PayloadSaveLeadData } from "@interfaces/request";
import { calculateAmountMonthly } from "@helpers/funciones";
import { TerminosCondiciones } from "@components/TerminosCondiciones";
import {
  EMAIL,
  PASSWORD,
  PASSWORD_ALLOWED_CHARS,
  HAS_SPECIAL_CHARACTERS,
  HAS_NUMBERS,
  HAS_UPPERCASE,
  HAS_LOWERCASE,
  HAS_WHITESPACE,
} from "@utils/expreciones";
import { signOut } from "aws-amplify/auth";
import { UUID } from "crypto";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { ApiError } from "@interfaces/response";
import { QuoterDetails } from "@interfaces/store";
import CustomRightImage from "@components/CustomRightImagev2";
import useInterfaceConfig from "@hooks/useInterfaceConfig";

const domainOptions = ["gmail.com", "outlook.com", "hotmail.com"];

const CrearCuenta = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const location = useLocation();
  const interfaceConfig = useInterfaceConfig();

  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  const [saveLeadData] = useSaveLeadDataMutation();

  const [showPassword, setShowPassword] = useState<boolean>(false);

  const formik = useFormik({
    initialValues: {
      email: accountUser.email || "",
      password: accountUser.password || "",
    },
    validationSchema: yup.object().shape({
      email: yup
        .string()
        .required("Campo requerido")
        .matches(EMAIL, "Asegúrate de incluir el @ y los puntos necesarios (.com .mx)"),
      password: yup
        .string()
        .required("Campo requerido")
        .test("password_rules", function (value) {
          if (!value) return true;

          if (value.length > 50) {
            return this.createError({ message: "Excede la longitud permitida (50 caracteres)." });
          }

          if (!PASSWORD_ALLOWED_CHARS.test(value)) {
            return this.createError({
              message: "Estás usando caracteres no permitidos. Intenta con otros caracteres.",
            });
          }

          if (!PASSWORD.test(value)) {
            return this.createError({
              message:
                "Formato incorrecto. Debe contener una letra mayúscula, número, carácter especial y más de 8 caracteres.",
            });
          }

          return true;
        }),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (!formik.isValid) {
          return;
        }

        setSubmitting(true);
        const payloadSaveLeadData: PayloadSaveLeadData = {
          email: values.email,
          amount: application.loan_amount,
          product_id: application.product_id,
          tax_system_id: application.tax_system_id,
          term: application.loan_term,
        };
        await saveLeadData(payloadSaveLeadData).unwrap();
        await signOut();

        dispatch(setLoanStatus("CSRUS"));
        dispatch(
          setAccountUser({
            id: 0,
            email: values.email,
            password: values.password,
          }),
        );

        navigate("/producto/creditosimple/registro_datos_personales");
      } catch (error) {
        const api_error = error as ApiError;
        if (api_error.data) {
          if (api_error.data.code === "001") {
            formik.setFieldError("email", "Este correo ya esta registrado, intenta con otro o inicia sesión");
            return;
          }
        }

        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const isLengthValid = formik.values.password.length >= 8 && formik.values.password.length <= 50;
  const hasNumber = HAS_NUMBERS.test(formik.values.password);
  const hasSpecialChar = HAS_SPECIAL_CHARACTERS.test(formik.values.password);
  const hasUpperCase = HAS_UPPERCASE.test(formik.values.password);
  const hasLowerCase = HAS_LOWERCASE.test(formik.values.password);
  const hasWhitespace = HAS_WHITESPACE.test(formik.values.password);

  const getDomainSuggestions = (value: string): string[] => {
    const parts = value.split("@");
    if (parts.length > 1) {
      const enteredDomain = parts[1].toLowerCase();
      return domainOptions
        .filter((option) => option.startsWith(enteredDomain))
        .map((domain) => `${parts[0]}@${domain}`);
    }
    return [];
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  useEffect(() => {
    const getQueryParams = () => {
      try {
        const searchParams = new URLSearchParams(location.search);
        const tax_system_id = searchParams.get("tax_system_id") as UUID;
        const product_id = searchParams.get("product_id") as UUID;
        const taxSystemType = searchParams.get("tax_system_type");
        const loanAmount = Number(searchParams.get("loan_amount"));
        const loanTerm = Number(searchParams.get("loan_term"));
        const sourceCode = String(searchParams.get("source_code") || "");
        const amountMonthly = calculateAmountMonthly(loanAmount, loanTerm);
        const financed_services_id = searchParams.get("financed_services_id") ?? null;
        const guarantee_type_id = searchParams.get("guarantee_type_id") ?? null;
        const is_previous_client_param = searchParams.get("is_previous_client");
        const description_guarantee = String(searchParams.get("description_guarantee") || "");
        const taxSystemCode = searchParams.get("person_type");
        const rfcCompany = searchParams.get("rfcCompany");
        const advance_percentage = Number(searchParams.get("advance_percentage"));
        const residual_percentage = Number(searchParams.get("residual_percentage"));
        const guarantee_types = searchParams.get("guarantee_types");
        const guarantee_types_array = (guarantee_types ?? "")
          .split(",")
          .map((value) => value.trim())
          .filter((value) => value.length > 0);
        const motorbike_catalog_id = searchParams.get("motorbike_catalog_id") ?? undefined;
        const convenio_id = searchParams.get("convenio_id") ?? null;

        const quoterDetailsParam = searchParams.get("quoter_details");
        let quoter_details: QuoterDetails | null = null;

        if (quoterDetailsParam) {
          quoter_details = JSON.parse(quoterDetailsParam);
        }

        const is_previous_client =
          is_previous_client_param === "true" ? true : is_previous_client_param === "false" ? false : null;

        if (!tax_system_id || !product_id || !taxSystemType || !loanAmount || !loanTerm) {
          navigate("/login");
          return;
        }

        if (taxSystemType !== "PM" && taxSystemType !== "PFAE" && taxSystemType !== "PF") {
          navigate("/login");
          return;
        }

        localStorage.removeItem("isLoggedOut");

        dispatch({ type: "CLEAN_STORE" });
        dispatch(setTaxSystemType(taxSystemType));
        dispatch(
          setTaxProductId({
            tax_system_id,
            product_id,
            source_code: sourceCode,
            type: String(taxSystemCode),
          }),
        );
        dispatch(
          setConditionLoan({
            loanAmount,
            loanTerm,
            amountMonthly,
            advance_percentage: isNaN(advance_percentage) ? null : advance_percentage,
            residual_percentage: isNaN(residual_percentage) ? null : residual_percentage,
          }),
        );
        dispatch(
          setGuaranteeTypeId({
            guarantee_type_id,
            description_guarantee,
          }),
        );
        dispatch(
          setGuaranteeTypes({
            guarantee_types: guarantee_types_array,
          }),
        );
        dispatch(
          setFinancedServicesId({
            financed_services_id,
          }),
        );
        dispatch(
          setIsPreviousClient({
            is_previous_client,
          }),
        );
        dispatch(setMotorbikeInformation({ motorbikeCatalogId: motorbike_catalog_id }));
        if (rfcCompany && rfcCompany.trim() !== "") {
          dispatch(setRfcApprovedCompany({ rfc_approved_company: rfcCompany }));
        }
        if (convenio_id) {
          dispatch(setConvenioId({ convenio_id }));
        }
        if (quoter_details) {
          dispatch(setQuoterDetails(quoter_details));
        }
      } catch (error) {
        console.log("Ha ocurrido un error", error);
      }
    };

    getQueryParams();
  }, []);

  return (
    <Grid container height={"100%"}>
      <Grid item xs={12} md={8} mt={4}>
        <Box
          sx={{
            mt: 2,
            mx: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant={isMobileOrTablet ? "h4" : "h3"}
            fontWeight={theme?.palette?.fontWeights?.bold}
            textAlign="center"
            color={theme?.palette?.primary?.dark}
          >
            {interfaceConfig.textos_tofu?.pm?.crear_cuenta?.titulo || "Te damos la bienvenida"}
          </Typography>

          <Typography
            mt={isMobileOrTablet ? 2 : 3}
            variant={isMobileOrTablet ? "body2" : "body1"}
            color={theme?.palette?.primary?.main}
          >
            Ingresa los datos para crear tu cuenta
          </Typography>

          <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 3 }}>
            <form onSubmit={formik.handleSubmit} noValidate>
              <Box>
                <Autocomplete
                  freeSolo
                  options={getDomainSuggestions(formik.values.email)}
                  inputValue={formik.values.email}
                  onInputChange={(_e, newInputValue) => {
                    const low = newInputValue.toLowerCase().trim();
                    formik.setFieldValue("email", low);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      id="email"
                      name="email"
                      label="Correo electrónico"
                      margin="normal"
                      required
                      fullWidth
                      autoComplete="off"
                      onBlur={formik.handleBlur}
                      error={formik.touched.email && Boolean(formik.errors.email)}
                      helperText={formik.touched.email && formik.errors.email}
                      inputProps={{ ...params.inputProps, maxLength: 100 }}
                    />
                  )}
                  disabled={formik.isSubmitting}
                />
                <TextField
                  id="password"
                  name="password"
                  label="Contraseña"
                  type={showPassword ? "text" : "password"}
                  margin="normal"
                  required
                  fullWidth
                  autoComplete="off"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.password}
                  error={formik.touched.password && Boolean(formik.errors.password)}
                  helperText={formik.touched.password && formik.errors.password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          edge="end"
                          sx={{ color: theme?.palette?.primary?.main }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  disabled={formik.isSubmitting}
                />
              </Box>
              <Box mt={isMobileOrTablet ? 2 : 3}>
                <Typography
                  variant={isMobileOrTablet ? "body2" : "body1"}
                  fontWeight={500}
                  color={theme?.palette?.primary?.dark}
                >
                  Tu contraseña debe contener:
                </Typography>

                <Typography
                  mt={1}
                  variant={isMobileOrTablet ? "body2" : "body1"}
                  color={isLengthValid ? "green" : "red"}
                  display="flex"
                  alignItems="center"
                >
                  {isLengthValid ? <CheckIcon /> : <CloseIcon />} &nbsp;Entre 8 y 50 caracteres
                </Typography>

                <Typography
                  variant={isMobileOrTablet ? "body2" : "body1"}
                  color={hasNumber ? "green" : "red"}
                  display="flex"
                  alignItems="center"
                >
                  {hasNumber ? <CheckIcon /> : <CloseIcon />} &nbsp;Un carácter numérico
                </Typography>

                <Typography
                  variant={isMobileOrTablet ? "body2" : "body1"}
                  color={hasSpecialChar ? "green" : "red"}
                  display="flex"
                  alignItems="center"
                >
                  {hasSpecialChar ? <CheckIcon /> : <CloseIcon />} &nbsp;Un carácter especial
                </Typography>

                <Typography
                  variant={isMobileOrTablet ? "body2" : "body1"}
                  color={hasUpperCase ? "green" : "red"}
                  display="flex"
                  alignItems="center"
                >
                  {hasUpperCase ? <CheckIcon /> : <CloseIcon />} &nbsp;Una mayúscula
                </Typography>

                <Typography
                  variant={isMobileOrTablet ? "body2" : "body1"}
                  color={hasLowerCase ? "green" : "red"}
                  display="flex"
                  alignItems="center"
                >
                  {hasLowerCase ? <CheckIcon /> : <CloseIcon />} &nbsp;Una minúscula
                </Typography>

                <Typography
                  variant={isMobileOrTablet ? "body2" : "body1"}
                  color={!hasWhitespace ? "green" : "red"}
                  display="flex"
                  alignItems="center"
                >
                  {!hasWhitespace ? <CheckIcon /> : <CloseIcon />} &nbsp;Sin espacios en blanco
                </Typography>
              </Box>

              <Box display="flex" justifyContent="center" mt={isMobileOrTablet ? 2 : 3} mb={2}>
                <Button
                  variant="default"
                  type="submit"
                  color="primary"
                  disabled={!formik.isValid || formik.isSubmitting}
                >
                  Continuar
                </Button>
              </Box>
            </form>
            <Box sx={{ mt: 4, mb: 2 }}>
              <TerminosCondiciones />
            </Box>
          </Container>
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default CrearCuenta;
